// libs
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';

// helpers
import Toast from '@common/helpers/toast';
import ApiHelper from '@/api/ApiHelper';

// interfaces
import { Patient } from '@/models/patient';
import { TreatmentEpisode } from '@/models/treatment-episode';

// constants
import { USER_STATUS } from '@common/constants/auth';

// services
import { fetchTreatmentEpisodesApi } from '@common/services/treatment-episodes';

@Module({ name: 'patient_detail', namespaced: true })
export default class Index extends VuexModule<any> {
  public treatmentEpisodes: TreatmentEpisode[] = [];
  public treatmentEpisodesLoading: boolean = false;
  public treatmentEpisodesLoadedPatientId: number | string | null = null;
  private data: Patient | {} = {};
  private isLoading = false;

  // ********* Mutations ********* //
  @Mutation
  public SET_TREATMENT_EPISODES(data: TreatmentEpisode[]) {
    this.treatmentEpisodes = data;
  }

  @Mutation
  public SET_TREATMENT_EPISODES_LOADING(val: boolean) {
    this.treatmentEpisodesLoading = val;
  }

  @Mutation
  public SET_TREATMENT_EPISODES_LOADED_PATIENT_ID(patientId: number | string | null) {
    this.treatmentEpisodesLoadedPatientId = patientId;
  }

  @Mutation
  public RESET_TREATMENT_EPISODES() {
    // Clears the cached episodes/loaded-patient marker so that switching to
    // a different patient (which reuses this component instance via
    // beforeRouteUpdate) refetches fresh data instead of showing the
    // previous patient's treatment history.
    this.treatmentEpisodes = [];
    this.treatmentEpisodesLoadedPatientId = null;
  }

  @Mutation
  public setPatient(patient) {
    if (patient.constructor.name === 'Object') {
      patient = new Patient().deserialize(patient);
    }
    this.data = patient;
  }

  @Mutation
  public setLoading(isLoading = true) {
    this.isLoading = isLoading;
  }

  // ********* Actions ********* //
  @Action({ rawError: true })
  public async fetchTreatmentEpisodes(patientId: number | string) {
    this.context.commit('SET_TREATMENT_EPISODES_LOADING', true);
    try {
      const episodes = await fetchTreatmentEpisodesApi(patientId);
      this.context.commit('SET_TREATMENT_EPISODES', episodes);
      this.context.commit('SET_TREATMENT_EPISODES_LOADED_PATIENT_ID', patientId);
    } finally {
      this.context.commit('SET_TREATMENT_EPISODES_LOADING', false);
    }
  }

  @Action({ rawError: true })
  public async getPatient(payload) {
    const { commit } = this.context;
    const { id } = payload;
    try {
      commit('setLoading');
      const response = await ApiHelper.getApi('PatientV2Api').detail(id);
      commit('setPatient', response);
    } catch (error) {
      commit('setLoading', false);
      const data = error.response.data;
      Toast.error(data.message);
    } finally {
      commit('setLoading', false);
    }
  }

  @Action({ rawError: true })
  public async updatePatient(payload) {
    const { commit } = this.context;
    const { id , data} = payload;
    try {
      commit('setLoading');
      const response = await ApiHelper.getApi('PatientApi').updateInformation(id, data);
      commit('setPatient', response);
      Toast.success('更新しました');
    } catch (error) {
      commit('setLoading', false);
      const data = error.response.data;
      Toast.error(data.message);
    } finally {
      commit('setLoading', false);
    }
  }

  @Action({ rawError: true })
  public async updateStateIdentification(payload) {
    const { commit } = this.context;
    const { id , data} = payload;
    try {
      commit('setLoading');
      await ApiHelper.getApi('PatientApi').identification(id, data);
      const response = await ApiHelper.getApi('PatientV2Api').detail(id);
      commit('setPatient', response);

      Toast.success(data.state === USER_STATUS.IDENTIFICATION_NG ? '本人確認NG' : '本人確認OK');
    } catch (error) {
      commit('setLoading', false);
      const data = error.response.data;
      Toast.error(data.message);
    } finally {
      commit('setLoading', false);
    }
  }

  @Action({ rawError: true })
  public async updateDeliveryAddress(payload) {
    const { commit } = this.context;
    const { id , data, address_id } = payload;
    try {
      commit('setLoading');
      await ApiHelper.getApi('PatientApi').updateDeliveryAddress(id, data, address_id);
      const response = await ApiHelper.getApi('PatientV2Api').detail(id);
      commit('setPatient', response);

      Toast.success('更新しました');
    } catch (error) {
      commit('setLoading', false);
      const data = error.response.data;
      Toast.error(data.message);
    } finally {
      commit('setLoading', false);
    }
  }
}
